import React, {useEffect, useLayoutEffect, useState} from 'react';
import {useJsApiLoader} from '@react-google-maps/api';
import {googleMapsProps} from '../../constants';
import {BarLoader} from 'react-spinners';
import {getSpecialPageFieldKeysForDatabase} from 'src/helpers/pages';
import CustomOneFilterBar from './CustomOneFilterBar';
import {BottomSheetRef} from 'react-spring-bottom-sheet';
import {PropagateLoader} from 'react-spinners';

import 'react-spring-bottom-sheet/dist/style.css';
import {format, IDatabaseField} from 'src/services/database';
import CustomOneTemplateModal from '../pages/CustomOneTemplateModal';
import {CustomOneFilter} from './layout/CustomOne';

const center = {
    lat: 30.4515,
    lng: -91.1871,
};

const parseQueryString = (queryString: string = window.location.search): Record<string, string[]> => {
    const params = new URLSearchParams(queryString.startsWith('?') ? queryString : `?${queryString}`);
    const result: Record<string, string[]> = {};

    Array.from(params.entries()).forEach(([key, value]) => {
        if (result[key]) {
            result[key].push(value);
        } else {
            result[key] = [value];
        }
    });

    return result;
}

const refreshWithFilters = (updatedFilters: Record<string, string[]>) => {
    const params = new URLSearchParams();

    Object.entries(updatedFilters).forEach(([key, values]) => {
        values.forEach(value => params.append(key, value));
    });

    // This will replace the URL and reload the page
    window.location.search = params.toString();
}

const specialKeys = [
    'page_title',
    'organization_name_or_collins_department',
    'resource_description',
    'program_focus_area',
    'resource_type',
    'resource_location',
    'identity_specifier',
    'ages_served',
    'grades_served',
    'program_availability',
    'cost',
    'contact_name',
    'contact_phone_number',
    'contact_email',
    'street_address',
    'city',
    'state',
    'zip_code',
    'website_link',
];

export default function CustomOneResults({
    database,
    results,
    intl,
    term,
    setTerm,
    pageId,
    setLocation,
    currentLocation,
    setSelectedFilters,
    selectedLocation,
    selectedFilters,
    setDistance,
    distance,
    onSearch,
    onClearFilters,
    loading,
    renderPager,
    totalRowCount,
    setType,
    type,
    onKeyDown,
    currentDistance,
    searchInput,
    setSearchInput,
}) {
    const [currentlyViewingPage, setCurrentlyViewingPage] = useState<string | null>(null);
    const [openItems, setOpenItems] = useState({});
    const [isMobile, setIsMobile] = useState(false);
    const mobileBreakpoint = 768; // Bootstrap MD breakpoint
    const collapseClasses = `col-md-4 collapse ${!isMobile ? 'show' : ''}`;

    const [hoveredResult, setHoveredResult] = useState(null);

    useLayoutEffect(() => {
        document?.getElementById?.('searchAddressBar')?.parentElement?.classList?.add?.('col');
    });

    useEffect(() => {
        setCurrentlyViewingPage(pageId);
    }, [pageId]);

    useEffect(() => {
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);

        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    const checkScreenSize = () => {
        const mobileView = window.innerWidth < mobileBreakpoint;
        setIsMobile(mobileView);

        // Crucially: When switching to mobile view, ensure all items are explicitly closed in state
        if (mobileView) {
            setOpenItems({}); // Resets all open items when entering mobile view
        }
    };

    const {
        title: titleKey,
        description: descKey,
        map: mapKey,
    }: any = database ? getSpecialPageFieldKeysForDatabase(specialKeys, database) : {};

    const handlePageClose = () => {
        if (currentlyViewingPage === pageId) onSearch(searchInput, 1, undefined, undefined, true);
        setCurrentlyViewingPage(null);
    };

    const renderSearchResultWrapper = (content: any) => {
        if (type === 'grid')
            return (
                <div className="col-lg-4 col-sm-12 col-md-6">
                    <div className="liveviewCon">{content}</div>
                </div>
            );
        return content;
    };

    const setFilter = (fieldName: string, value: string, allowMany: boolean = true) => {
        const filters = parseQueryString();
        const prev = Array.isArray(filters[fieldName]) ? filters[fieldName] : [];
        let next: string[];

        if (prev.includes(value)) {
            // Remove the value
            next = prev.filter(v => v !== value);
        } else if (allowMany) {
            // Add the value (keep multiple selected)
            next = [...prev, value];
        } else {
            // Only allow one value selected at a time
            next = [value];
        }

        // Update the filters
        const updatedFilters = { ...filters };
        if (next.length > 0) {
            updatedFilters[fieldName] = next;
        } else {
            // Remove field if none selected
            delete updatedFilters[fieldName];
        }

        refreshWithFilters(updatedFilters);
    };

    const renderAttribute = (value: string, icon: any) => (
        <div className="col-md-6" style={{display: 'flex', alignItems: 'center'}}>
            {icon}
            <span className="text-muted ml-2" style={{fontSize: 14}}>
                {value}
            </span>
        </div>
    );

    const renderAttributes = (item: any) => {
        const {
            contact_name,
            contact_phone_number,
            contact_email,
            website_link,
        } = getSpecialPageFieldKeysForDatabase(specialKeys, database) as any;

        return (
            <div className="row">
                {item[contact_name] &&
                    renderAttribute(
                        JSON.parse(item[contact_name]),
                        <em className="fas fa-user text-muted pt-1" style={{fontSize: 12}} />
                    )}
                {item[contact_phone_number] &&
                    renderAttribute(
                        JSON.parse(item[contact_phone_number]),
                        <em className="fas fa-phone text-muted pt-1" style={{fontSize: 12}} />
                    )}
                {item[contact_email] &&
                    renderAttribute(
                        JSON.parse(item[contact_email]),
                        <em className="fas fa-envelope text-muted pt-1" style={{fontSize: 12}} />
                    )}    
                {item[website_link] &&
                    renderAttribute(
                        JSON.parse(item[website_link]),
                        <em className="fas fa-globe text-muted pt-1" style={{fontSize: 12}} />
                    )}
            </div>
        );
    };

    const renderSearchResult = (item: any, i: number) => {
        try {
            if (!database) return;

            const title = item[titleKey] && JSON.parse(item[titleKey]);
            const desciption = item[descKey] && JSON.parse(item[descKey]);

            const image = item.account_organization_avatar;
            let bgImage = {};
            if (image !== '(none)') {
                bgImage = {
                    backgroundImage: `url("${image.replace(/ /g, '%20')}")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundSize: '300px 168.75px',
                };
            }
            return renderSearchResultWrapper(
                <a
                    key={item.name}
                    style={{
                        textDecoration: 'none',
                        color: 'black',
                        cursor: 'pointer',
                    }}
                    className="row py-2 px-4 px-lg-3"
                    data-toggle="modal"
                    data-target={`#detailModal`}
                    onClick={() => setCurrentlyViewingPage(item.name)}
                    onMouseEnter={() => setHoveredResult(item.name)}>
                    <div className="d-flex w-100">
                        {image !== '(none)' && (
                            <div
                                className="col border-top border-bottom border-left rounded-left px-1 image"
                                style={{
                                    ...bgImage,
                                    overflow: 'hidden',
                                    backgroundSize: 'contain',
                                    maxWidth: 150,
                                }}>
                                {image ? (
                                    <span />
                                ) : (
                                    <div className="mx-auto">
                                        <BarLoader height={4} width={300} />
                                    </div>
                                )}
                            </div>
                        )}
                        <div
                            className="col border rounded-right p-lg-3 p-2"
                            style={hoveredResult === item.name ? styles.boxShadow : {}}>
                            <div className="flex-direction-row">
                                <h4>{title}</h4>
                                <h5 style={{color: '#595959'}}>
                                    {item?.account_organization_name}
                                </h5>
                            </div>
                            <div className="p">{renderAttributes(item)}</div>
                        </div>
                    </div>
                </a>
            );
        } catch (ex) {}
        return null;
    };

    const renderResultCount = () => {
        if (!results?.length)
            return (
                <div className="text-center">
                    <h3>No Results Found</h3>
                </div>
            );
        return (
            <div>
                <h2 className="text-muted ml-4">
                    {totalRowCount} Result{totalRowCount !== 1 ? 's' : ''} Found
                </h2>
            </div>
        );
    };

    const sortList = (list: string[], databaseField: IDatabaseField) => {
        return list.sort((a, b) =>
            Array.isArray(databaseField.options)
                ? databaseField.options.indexOf(a) - databaseField.options.indexOf(b)
                : 0
        );
    };

    return (
        <>
            {currentlyViewingPage ? (
                <CustomOneTemplateModal
                    pageId={currentlyViewingPage}
                    database={database}
                    onClose={handlePageClose}
                />
            ) : null}

            <div className="container" style={{height: 65, width: '100%', boxShadow: '0 0 3px 0 rgba(0,0,0,.1)'}}>
                <CustomOneFilterBar
                    database={database}
                    intl={intl}
                    term={searchInput}
                    setTerm={setSearchInput}
                    setLocation={setLocation}
                    location={selectedLocation || currentLocation}
                    filter={selectedFilters}
                    setDistance={setDistance}
                    distance={distance}
                    setFilter={setSelectedFilters}
                    onSearch={() => onSearch(searchInput, 1)}
                    onClearFilters={onClearFilters}
                    onKeyDown={onKeyDown}
                />
            </div>
            <div className="container" style={{minHeight: '100%'}}>
                <div className="row">
                    <div
                        className={collapseClasses}
                        id='collapseFilters'
                        style={{
                            padding: 20,
                            borderRight: '1px solid #eee',
                            background: '#fafbfc',
                            overflowY: 'auto',
                            height: window.innerHeight - 87.5 - 75,
                        }}>
                        {
                            // Declare filters variable first
                            (() => {
                                const query = parseQueryString();
                                const filters = database && database.filters ? database.filters : [];
                                return (
                                    !database ||
                                    filters.map((x) => {
                                        const field = database.fields.find(
                                            (f) => format(f.group, f.name) === x
                                        );
                                        if (!field) return null;
                                        return (
                                            <CustomOneFilter
                                                key={x}
                                                id={x}
                                                field={field}
                                                query={query}
                                                onChange={setFilter}
                                            />
                                        );
                                    })
                                );
                            })()
                        }
                    </div>

                    {/* Results Area */}
                    <div
                        className="results-col col-md-8"
                        onMouseLeave={() => setHoveredResult(null)}>
                        {results.map(renderSearchResult)}
                        {loading ? (
                            <div className="p-5 d-flex justify-content-center">
                                <PropagateLoader size={15} loading={loading} color="#29447b" />
                            </div>
                        ) : (
                            !results?.length && (
                                <div className="text-center mt-4">
                                    <h3>No Results Found</h3>
                                    <div>Try changing your filters!</div>
                                </div>
                            )
                        )}
                        <div className="searchfiltercontent">{renderPager()}</div>
                    </div>
                </div>
            </div>
        </>
    );
}

const styles = {
    boxShadow: {
        backgroundColor: '#f5f5f5',
    },
};



// WEBPACK FOOTER //
// ./src/public/search/CustomOneResults.tsx